/**
 * ChatFilterDefinitions - Definições de filtros para views do chat
 */

// ═══════════════════════════════════════════════════════
// FILTER TYPES
// ═══════════════════════════════════════════════════════

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterDefinition {
  key: string;
  label: string;
  type: 'multiselect' | 'toggle' | 'daterange' | 'funnel-steps';
  options?: FilterOption[];
  placeholder?: string;
  /** Se true, opções são carregadas via API (teams, users) */
  dynamic?: boolean;
}

export interface FilterCategory {
  id: string;
  label: string;
  filters: string[];
}

// ═══════════════════════════════════════════════════════
// STATIC OPTIONS
// ═══════════════════════════════════════════════════════

export const STATUS_OPTIONS: FilterOption[] = [
  { value: 'waiting', label: 'Aguardando' },
  { value: 'active', label: 'Ativo' },
  { value: 'closed', label: 'Fechado' },
];

export const PRIORITY_OPTIONS: FilterOption[] = [
  { value: 'urgent', label: 'Urgente' },
  { value: 'high', label: 'Alta' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Baixa' },
];

export const CHANNEL_OPTIONS: FilterOption[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'widget', label: 'Widget de Chat' },
];

// ═══════════════════════════════════════════════════════
// FILTER DEFINITIONS PER VIEW
// ═══════════════════════════════════════════════════════

export const CONVERSATION_FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  status: {
    key: 'status',
    label: 'Status',
    type: 'multiselect',
    options: STATUS_OPTIONS,
    placeholder: 'Buscar status...',
  },
  priority: {
    key: 'priority',
    label: 'Prioridade',
    type: 'multiselect',
    options: PRIORITY_OPTIONS,
    placeholder: 'Buscar prioridade...',
  },
  channelType: {
    key: 'channelType',
    label: 'Tipo de canal',
    type: 'multiselect',
    options: CHANNEL_OPTIONS,
    placeholder: 'Buscar tipo de canal...',
  },
  channelId: {
    key: 'channelId',
    label: 'Canal',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar canal...',
  },
  teamId: {
    key: 'teamId',
    label: 'Equipe',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar equipe...',
  },
  assigneeId: {
    key: 'assigneeId',
    label: 'Operador',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar operador...',
  },
  tags: {
    key: 'tags',
    label: 'Tags',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar tag...',
  },
  stepId: {
    key: 'stepId',
    label: 'Etapa no funil',
    type: 'funnel-steps',
    placeholder: 'Buscar etapa...',
  },
  contactCustomerId: {
    key: 'contactCustomerId',
    label: 'Cliente',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar cliente...',
  },
  hasUnreadMessages: {
    key: 'hasUnreadMessages',
    label: 'Não lidas',
    type: 'toggle',
  },
  dateRange: {
    key: 'dateRange',
    label: 'Período',
    type: 'daterange',
  },
};

export const CONTACT_FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  tags: {
    key: 'tags',
    label: 'Tags',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar tag...',
  },
  channelType: {
    key: 'channelType',
    label: 'Tipo de canal',
    type: 'multiselect',
    options: CHANNEL_OPTIONS,
    placeholder: 'Buscar tipo de canal...',
  },
};

// ═══════════════════════════════════════════════════════
// CATEGORIES
// ═══════════════════════════════════════════════════════

export const CONVERSATION_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'status',
    label: 'Status',
    filters: ['status', 'priority'],
  },
  {
    id: 'channel',
    label: 'Canal',
    filters: ['channelType', 'channelId'],
  },
  {
    id: 'assignment',
    label: 'Atribuição',
    filters: ['teamId', 'assigneeId'],
  },
  {
    id: 'tags',
    label: 'Tags',
    filters: ['tags'],
  },
  {
    id: 'crm',
    label: 'CRM',
    filters: ['stepId', 'contactCustomerId'],
  },
  {
    id: 'other',
    label: 'Outros',
    filters: ['hasUnreadMessages', 'dateRange'],
  },
];

export const CONTACT_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'tags',
    label: 'Tags',
    filters: ['tags'],
  },
  {
    id: 'channel',
    label: 'Canal',
    filters: ['channelType'],
  },
];

export const GROUPS_FILTER_DEFINITIONS: Record<string, FilterDefinition> = {
  channelType: {
    key: 'channelType',
    label: 'Tipo de canal',
    type: 'multiselect',
    options: CHANNEL_OPTIONS,
    placeholder: 'Buscar tipo de canal...',
  },
  channelId: {
    key: 'channelId',
    label: 'Canal',
    type: 'multiselect',
    dynamic: true,
    placeholder: 'Buscar canal...',
  },
};

export const GROUPS_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'channel',
    label: 'Canal',
    filters: ['channelType', 'channelId'],
  },
];

// ═══════════════════════════════════════════════════════
// CHAT FILTERS STATE
// ═══════════════════════════════════════════════════════

export interface ChatFiltersState {
  /** Busca por texto (nome do contato, assunto, mensagem) */
  search?: string;
  status?: string[];
  priority?: string[];
  channelType?: string[];
  channelId?: string[];
  teamId?: string[];
  assigneeId?: string[];
  hasUnreadMessages?: boolean;
  dateFrom?: string;
  dateTo?: string;
  tags?: string[];
  /** Filtro "Etapa no funil": stepIds selecionados (FunnelStep._id) */
  stepId?: string[];
  /** Filtro "Cliente": customerIds selecionados (Contact.customerId) */
  contactCustomerId?: string[];
  /** Legacy: "Meus atendimentos" toggle */
  assigned?: boolean;
}

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

export interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
}

function formatDate(dateStr: string): string {
  try {
    const [year, month, day] = dateStr.split('-');
    if (year && month && day) return `${day}/${month}`;
    return dateStr;
  } catch {
    return dateStr;
  }
}

export function getActiveFilters(
  filters: ChatFiltersState,
  definitions: Record<string, FilterDefinition>,
  dynamicOptions?: Record<string, FilterOption[]>
): ActiveFilter[] {
  const entries: ActiveFilter[] = [];

  // Search is now always visible in the header input (no chip needed)

  const arrayKeys = ['status', 'priority', 'channelType', 'channelId', 'teamId', 'assigneeId', 'tags', 'contactCustomerId'] as const;

  for (const key of arrayKeys) {
    const value = filters[key];
    if (!value || !Array.isArray(value) || value.length === 0) continue;

    const def = definitions[key];
    if (!def) continue;

    const options = def.dynamic && dynamicOptions?.[key]
      ? dynamicOptions[key]
      : def.options || [];

    const labels = value
      .map(v => options.find(o => o.value === v)?.label || v)
      .filter(Boolean);

    if (labels.length === 0) continue;

    let displayValue: string;
    if (labels.length <= 2) displayValue = labels.join(', ');
    else displayValue = `${labels[0]}, +${labels.length - 1}`;

    entries.push({ key, label: def.label, displayValue });
  }

  // Etapa no funil: rótulo por contagem (nomes das etapas são carregados lazy por funil,
  // então não há mapa global stepId→nome para resolver o label aqui)
  if (filters.stepId && filters.stepId.length > 0) {
    const n = filters.stepId.length;
    entries.push({ key: 'stepId', label: 'Etapa no funil', displayValue: `${n} etapa${n > 1 ? 's' : ''}` });
  }

  // Toggle: hasUnreadMessages
  if (filters.hasUnreadMessages) {
    entries.push({ key: 'hasUnreadMessages', label: 'Não lidas', displayValue: 'Sim' });
  }

  // Date range
  if (filters.dateFrom || filters.dateTo) {
    const fromStr = filters.dateFrom ? formatDate(filters.dateFrom) : '';
    const toStr = filters.dateTo ? formatDate(filters.dateTo) : '';
    let displayValue = '';
    if (fromStr && toStr) displayValue = `${fromStr} - ${toStr}`;
    else if (fromStr) displayValue = `a partir de ${fromStr}`;
    else if (toStr) displayValue = `até ${toStr}`;

    entries.push({ key: 'dateRange', label: 'Período', displayValue });
  }

  return entries;
}

/**
 * Conta grupos de filtros ativos (exclui a busca, que tem input próprio no header).
 * Usado para o badge de contagem no botão de filtros. Mesma semântica de
 * getActiveFilters, sem depender de definitions/dynamicOptions.
 */
export function countActiveFilters(filters: ChatFiltersState): number {
  let count = 0;

  const arrayKeys = ['status', 'priority', 'channelType', 'channelId', 'teamId', 'assigneeId', 'tags'] as const;
  for (const key of arrayKeys) {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) count++;
  }

  if (filters.hasUnreadMessages) count++;
  if (filters.dateFrom || filters.dateTo) count++;

  return count;
}
